/**
 * 要件定義pt 明細を**母票（親チケット）単位**へ畳む純関数。
 *
 * 要件定義セットは 1 案件につき 5 工程（業務フロー図作成 / バリデーションリスト作成 /
 * 機能一覧作成 / 要件定義レビュー / 総合テスト作成）の子票が自動生成される。子票を素のまま
 * 並べると、同じ案件の 5 行が繰り返し出て「何案件やっているのか」が読めない。明細モーダルは
 * 母票ごとに畳み、母票の見出し行 + その配下の子票、という形で出す。
 *
 * 未対応（待処理）側は API が既に母票単位で畳んだ `projects` を返すので、そちらは
 * `projectsToDetailGroups` で同じ形へ写すだけ（列の値と明細の合計がずれないよう、
 * 畳み方をフロントで作り直さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義pt 明細（完了 / 処理中）を母票単位へ畳み、母票の pt 降順で並べる。未対応は API が畳んだ結果を同じ形へ写す
 * @graph-connects none
 */

import type { RequirementPendingProject } from '../../types/combat-power.js';
import type { MemberPtRow } from './member-pt-breakdown.js';

/** 明細モーダルの 1 グループ（母票 1 件分）。 @graph-connects none */
export interface RequirementDetailGroup {
  /** 母票の Backlog チケットキー。親票が引けなかった子票は null */
  parentIssueKey: string | null;
  /** 母票の件名。引けなければ null */
  parentSummary: string | null;
  /** この母票配下の pt 合計 */
  point: number;
  /** この母票配下の子票 */
  rows: MemberPtRow[];
}

/**
 * pt 合計の丸め（小数第1位）。素直に足すと `12.299999999999999` のような端数が出て、
 * 母票見出しの合計と節の合計がわずかにずれる。
 * @graph-connects none
 */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * グループを pt の降順（同値は母票キー順、親不明は最後）で並べる。
 * 「どの案件が重いか」から読めるようにするため。
 * @graph-connects none
 */
function sortGroups(groups: RequirementDetailGroup[]): RequirementDetailGroup[] {
  return groups.sort((a, b) => {
    if (a.parentIssueKey === null) return 1;
    if (b.parentIssueKey === null) return -1;
    if (b.point !== a.point) return b.point - a.point;
    return a.parentIssueKey.localeCompare(b.parentIssueKey);
  });
}

/**
 * 母票キーを持たない行の受け皿キー。キーが null の行どうしは束ねる先が無いため 1 つへ寄せる。
 * 実在のチケットキーと衝突しないよう、キーに使えない空白始まりの文字列にする。
 * @graph-connects none
 */
const UNKNOWN_PARENT_KEY = ' unknown';

/**
 * 明細行を母票単位へ畳む。母票キーを持たない行は「親不明」の 1 グループにまとめる
 * （落とすと節の合計が pt 列と合わなくなる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義pt の明細行を母票キーでグルーピングし、母票ごとの pt 合計を付けて pt 降順で返す
 * @graph-connects none
 */
export function groupRowsByParent(rows: readonly MemberPtRow[]): RequirementDetailGroup[] {
  const byParent = new Map<string, RequirementDetailGroup>();
  for (const row of rows) {
    const key = row.parentIssueKey ?? UNKNOWN_PARENT_KEY;
    let group = byParent.get(key);
    if (!group) {
      group = {
        parentIssueKey: row.parentIssueKey ?? null,
        parentSummary: row.parentSummary ?? null,
        point: 0,
        rows: [],
      };
      byParent.set(key, group);
    }
    group.rows.push(row);
    group.point = round1(group.point + row.point);
  }
  return sortGroups([...byParent.values()]);
}

/**
 * API が母票単位で返した未対応明細を、完了 / 処理中と同じグループ形へ写す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 未対応の要件定義（API が母票単位で畳んだ結果）を明細モーダルのグループ形へ変換する
 * @graph-connects none
 */
export function projectsToDetailGroups(
  projects: readonly RequirementPendingProject[],
): RequirementDetailGroup[] {
  return projects.map((project) => ({
    parentIssueKey: project.parentIssueKey,
    parentSummary: project.parentSummary,
    point: project.point,
    rows: project.tickets.map((ticket) => ({
      issueKey: ticket.issueKey,
      summary: ticket.summary,
      issueTypeName: ticket.issueTypeName,
      point: ticket.point,
      completedDate: null,
      parentIssueKey: project.parentIssueKey,
      parentSummary: project.parentSummary,
    })),
  }));
}
